import React, { useEffect, useState } from "react";
import { Button, Modal, Form, InputGroup } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

import {
  emptyString,
  errorMessages,
  inputFieldTypes,
  loginConstants,
  toastTypes,
  PASSWORD_LENGTH_ERROR,
  PASSWORD_VALIDATION_PATTERN,
  passwordValidationMessages,
} from "../../../../constants";
import {
  userPermissionsConstants,
  userPermissionsFieldsNames,
} from "../../../../views/shared-screens/constants";
import { getItemFromStorage } from "../../../../system";
import { userPermissionsActions } from "../../../../views/shared-screens/redux/actions";
import {
  customToast,
  getClassName,
  getPasswordValidationState,
} from "../../../utility";
import { Spinner } from "../../spinner/spinner";
import PasswordValidationMessage from "./passwordValidationMessage";
import { useSpinner } from "../../hooks";

const { TEXT, PASSWORD } = inputFieldTypes;

const ResetPasswordUserPermission = (props) => {
  const {
    resetPasswordResponseMsg,
    resetPasswordResponse,
    SubmitButtonVariant,
    SubmitButtonStyle,
    SubmitButtonText,
    resetPassword,
    ModalSize,
    onClose,
    animate,
    userId,
    Title,
    show,
  } = props;

  const [data, setData] = useState({
    currentPassword: emptyString,
    newPassword: emptyString,
    confirmPassword: emptyString,
  });
  const [checkValidation, setCheckValidation] = useState({
    checkSpecialChar: false,
    checkCapLetter: false,
    checkNumber: false,
    checkLength: false,
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  let localRole = getItemFromStorage(loginConstants.ROLE);

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm();

  //Function to handle change in fields
  const handleChange = (e) => {
    const { value, name } = e.target;
    if (name === userPermissionsFieldsNames.NEW_PASSWORD) {
      setCheckValidation(
        getPasswordValidationState(value, emptyString, {
          includeEquality: false,
        }),
      );
    }
    setData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  //OnSubmit function to submit data on submission
  const onSubmit = () => {
    if (data.newPassword === data.confirmPassword) {
      let body = {
        id: userId,
        newPassword: data.newPassword,
        ...(localRole !== userPermissionsConstants.SUPER_ADMIN && {
          currentPassword: data.currentPassword,
        }),
      };
      resetPassword(body);
    } else {
      customToast(errorMessages.PASSWORD_NOT_MATCHED_ERROR, toastTypes.ERROR);
    }
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  const renderShowPasswordButton = (name, flag) => {
    return (
      <InputGroup.Text
        id={name}
        className={`${errors[name] && "border border-danger"}`}
      >
        <FontAwesomeIcon
          size="sm"
          data-testid={`${flag}-IconTestId`}
          icon={showPassword[flag] ? faEye : faEyeSlash}
          onClick={() =>
            setShowPassword((prev) => ({
              ...prev,
              [flag]: !prev[flag],
            }))
          }
          className="cursor-pointer"
        />
      </InputGroup.Text>
    );
  };

  //Close popup on success
  useEffect(() => {
    if (resetPasswordResponse) {
      onClose();
      resetPasswordResponseMsg();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPasswordResponse]);

  return (
    <>
      {spinner(
        userPermissionsConstants.RESET_PASSWORD_USER_PERMISSION_SPINNER
      ) ? (
        <Spinner />
      ) : (
        <Modal
          show={show}
          onHide={onClose}
          animation={animate}
          size={ModalSize}
          className="react-survey-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>{Title}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body className="mx-3">
              <Form.Group
                className={getClassName(
                  localRole === userPermissionsConstants.SUPER_ADMIN,
                  "d-none"
                )}
              >
                <Form.Label>Confirm Current Password:</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword.current ? TEXT : PASSWORD}
                    name="currentPassword"
                    data-testid="current-password"
                    value={data.currentPassword}
                    {...register("currentPassword", {
                      required:
                        localRole !== userPermissionsConstants.SUPER_ADMIN,
                      onChange: (e) => handleChange(e),
                    })}
                    className={`font-14-px text-secondary ${getClassName(
                      errors.currentPassword,
                      "is-invalid"
                    )}`}
                  />
                  {renderShowPasswordButton("currentPassword", "current")}
                </InputGroup>
              </Form.Group>
              <Form.Group className="mt-2">
                <Form.Label>New Password:</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword.new ? TEXT : PASSWORD}
                    name="newPassword"
                    value={data.newPassword}
                    data-testid="new-password"
                    {...register("newPassword", {
                      required: true,
                      onChange: (e) => handleChange(e),
                      pattern: {
                        value: PASSWORD_VALIDATION_PATTERN,
                      },
                    })}
                    className={`font-14-px text-secondary ${getClassName(
                      errors.newPassword,
                      "is-invalid"
                    )}`}
                  />
                  {renderShowPasswordButton("newPassword", "new")}
                </InputGroup>

                <Form.Text className="text-muted lh-1">
                  <ul className="pt-2 ms-0 ps-4">
                    <PasswordValidationMessage
                      isValid={checkValidation.checkNumber}
                      message={passwordValidationMessages.NUMBER}
                    />
                    <PasswordValidationMessage
                      isValid={checkValidation.checkCapLetter}
                      message={passwordValidationMessages.CAPITAL}
                    />
                    <PasswordValidationMessage
                      isValid={checkValidation.checkSpecialChar}
                      message={passwordValidationMessages.SPECIAL_CHAR}
                    />
                    <PasswordValidationMessage
                      isValid={checkValidation.checkLength}
                      message={PASSWORD_LENGTH_ERROR}
                    />
                  </ul>
                </Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label>Confirm New Password:</Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPassword.confirm ? TEXT : PASSWORD}
                    name="confirmPassword"
                    value={data.confirmPassword}
                    data-testid="confirm-password"
                    {...register("confirmPassword", {
                      required: true,
                      onChange: (e) => handleChange(e),
                    })}
                    className={`font-14-px text-secondary ${getClassName(
                      errors.confirmPassword,
                      "is-invalid"
                    )}`}
                  />
                  {renderShowPasswordButton("confirmPassword", "confirm")}
                </InputGroup>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button
                type="submit"
                className="mx-3"
                style={SubmitButtonStyle}
                variant={SubmitButtonVariant}
              >
                {SubmitButtonText}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  resetPasswordResponse: state.userPermissionsReducer.resetPasswordResponse,
});

const mapDispatchToProps = (dispatch) => ({
  resetPassword: (body) => dispatch(userPermissionsActions.resetPassword(body)),
  resetPasswordResponseMsg: () =>
    dispatch(userPermissionsActions.resetPasswordResponseMsg()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ResetPasswordUserPermission);
